/**
 * Cat & Mouse — the grid (specs/cat-mouse-mode.md §4.1, §4.3).
 *
 * Everything about the *shape* of a board lives here; `deal.ts` decides what glyphs go on it,
 * `walk.ts` decides what a step does, and `CatScene` only draws what they return. Phaser-free so it
 * can be tested in node, exactly like `game/maze/grid.ts` and `game/labyrinth/maze.ts`.
 *
 * **There are no walls.** An earlier build carved corridors out of the grid and drew them as dark
 * lines between cells; they were barely visible at this tile size and they were never doing any
 * work — what makes a board a puzzle here is the letters, not the geometry. With them gone the grid
 * is plain four-way adjacency, and the whole edge/corridor apparatus (an `open` set threaded through
 * every route function, a generator that removed edges while checking connectivity, and a test that
 * every cell stayed reachable) went with them rather than staying behind as a parameter that could
 * only ever hold one value.
 */

/** `[col, row]`. Column 0 is the entrance edge; column `cols - 1` is the door edge. */
export type Cell = readonly [number, number];

export type Rng = () => number;

export const cellKey = (c: Cell): string => `${c[0]},${c[1]}`;

/**
 * The four moves a step may ever be. **No diagonals** — they make the grid harder to read, and they
 * are how a child skips a letter they meant to step on (§5.1).
 */
export function neighbours([c, r]: Cell, cols: number, rows: number): Cell[] {
  const out: Cell[] = [];
  if (c > 0) out.push([c - 1, r]);
  if (c < cols - 1) out.push([c + 1, r]);
  if (r > 0) out.push([c, r - 1]);
  if (r < rows - 1) out.push([c, r + 1]);
  return out;
}

/** Can the cat step from `a` to `b`? Orthogonally adjacent and in bounds — that is the whole rule. */
export function canStep(a: Cell, b: Cell, cols: number, rows: number): boolean {
  return neighbours(a, cols, rows).some((n) => n[0] === b[0] && n[1] === b[1]);
}

/**
 * Cells reachable from `start`, never entering a cell in `blocked`.
 *
 * With nothing blocked this is the whole grid. With the decoys blocked it is the region a child can
 * walk without ever stepping on a letter they were not sent for — which is how "the way to a target
 * may never be blocked" is checked for *every* order at once, rather than for the one order the
 * board happened to be dealt in (see `cleanRouteExists`).
 */
export function reachable(
  start: Cell, cols: number, rows: number, blocked: Set<string> = new Set(),
): Set<string> {
  const seen = new Set<string>([cellKey(start)]);
  const queue: Cell[] = [start];
  while (queue.length > 0) {
    const cur = queue.shift() as Cell;
    for (const n of neighbours(cur, cols, rows)) {
      const k = cellKey(n);
      if (seen.has(k) || blocked.has(k)) continue;
      seen.add(k);
      queue.push(n);
    }
  }
  return seen;
}

/**
 * Shortest-path parents from `start`, never entering a cell in `blocked`.
 *
 * `blocked` is how the dealer keeps a route clear of the letters it is about to place: a path to a
 * target must not run *through* another target, or the child would be made to collect them in an
 * order the board never asked for.
 */
export function bfsParents(
  start: Cell, cols: number, rows: number, blocked: Set<string> = new Set(),
): Map<string, Cell | null> {
  const parent = new Map<string, Cell | null>([[cellKey(start), null]]);
  const queue: Cell[] = [start];
  while (queue.length > 0) {
    const cur = queue.shift() as Cell;
    for (const n of neighbours(cur, cols, rows)) {
      const k = cellKey(n);
      if (parent.has(k) || blocked.has(k)) continue;
      parent.set(k, cur);
      queue.push(n);
    }
  }
  return parent;
}

/**
 * The cells of the route from `start` to `goal`, or null when `blocked` walls it off.
 *
 * The goal itself may be blocked — it is where the walk ends, not somewhere it passes through.
 */
export function routeTo(
  goal: Cell, start: Cell, cols: number, rows: number, blocked: Set<string> = new Set(),
): Cell[] | null {
  const without = new Set(blocked);
  without.delete(cellKey(goal));
  const parent = bfsParents(start, cols, rows, without);
  if (!parent.has(cellKey(goal))) return null;
  const out: Cell[] = [];
  let at: Cell | null = goal;
  while (at) {
    out.unshift(at);
    at = parent.get(cellKey(at)) ?? null;
  }
  return out;
}
